import { useCallback, useEffect, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "underscore";
import * as locationsRepo from "../../../../shared/repos/graphql/locations";
import * as elementsActions from "../../../../redux/actions/elements";
import { setCaptchaToken } from "../../../../shared/utilities/captcha";
import captchaAction from "../../../../shared/constants/captchaAction";
import httpStatusConstant from "../../../../shared/constants/httpStatus";
import elementConstants from "../../../../shared/constants/element";

/**
 * @typedef {Object} AddressType
 * @property {number} id
 *
 * @typedef {Object} Address
 * @property {string} externalId
 * @property {string} address1
 * @property {AddressType} locationType
 */

/**
 * Checks if an error is a 401 Unauthorized error
 * @param {Error} error
 * @returns {boolean}
 */
const isUnauthorizedError = error => {
  const errorMessage = error?.message || error?.toString() || "";
  const networkError = error?.networkError;
  const statusCode = networkError?.statusCode;
  return (
    statusCode === 401 ||
    errorMessage.indexOf(httpStatusConstant.unauthorised) !== -1 ||
    (networkError?.result &&
      JSON.stringify(networkError.result).indexOf(
        httpStatusConstant.unauthorised
      ) !== -1)
  );
};

/**
 * @typedef {Object} SearchLocations
 * @property {string} value
 * @property {Error | null} error
 * @property {boolean} fetched
 * @property {boolean} loading
 * @property {Array<Address>} locations
 * @property {boolean} showCaptchaChallenge
 * @property {(value: string) => Promise<void>} fetch
 * @property {(token: string) => void} onCaptchaVerify
 *
 * @returns {SearchLocations}
 */
export default function useAddressSearch() {
  const dispatch = useDispatch();
  const useRecaptcha = useSelector(state => state.elements.useRecaptcha);

  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      value: "",
      error: null,
      fetched: false,
      loading: false,
      locations: undefined,
      showCaptchaChallenge: false,
      skipGenerateNewToken: false,
      lastSearchValue: null
    }
  );

  const setVerificationToken = value =>
    dispatch(elementsActions.setVerificationToken(value));
  const setVerificationAction = value =>
    dispatch(elementsActions.setVerificationAction(value));
  const setVerificationProvider = value =>
    dispatch(elementsActions.setVerificationProvider(value));

  const fetchLocations = useCallback(
    _.debounce(
      /**
       * @param {string} address
       * @param {boolean} skipCaptchaGeneration - Skip generating new captcha token (used after challenge)
       * @returns {Promise<void>}
       */
      async (address, skipCaptchaGeneration = false) => {
        if (address.length < 3) return;

        try {
          setState({
            loading: true,
            error: null,
            showCaptchaChallenge: false,
            lastSearchValue: address
          });

          // Generate captcha token before request if needed
          if (useRecaptcha === 1 && !skipCaptchaGeneration) {
            await setCaptchaToken(
              setVerificationToken,
              setVerificationAction,
              captchaAction.addressSearching
            );
          }

          const result = await locationsRepo.getLocations(
            address,
            null,
            null,
            false,
            true
          );
          setState({
            locations: result.data.locationSearch,
            skipGenerateNewToken: false
          });
          setVerificationProvider("");
        } catch (error) {
          // Check if it's a 401 error and captcha is enabled
          if (isUnauthorizedError(error) && useRecaptcha === 1) {
            setState({
              error,
              showCaptchaChallenge: true,
              locations: []
            });
          } else {
            setState({ error });
          }
        } finally {
          setState({ loading: false, fetched: true });
        }
      },
      500
    ),
    [
      useRecaptcha,
      setVerificationToken,
      setVerificationAction,
      setVerificationProvider
    ]
  );

  /**
   * Called after user completes captcha challenge
   * @param {string} token - The captcha token from the challenge
   */
  const onCaptchaVerify = useCallback(
    token => {
      if (token) {
        setVerificationToken(token);
        setVerificationProvider(elementConstants.challengeRecaptcha);
        setState({
          showCaptchaChallenge: false,
          skipGenerateNewToken: true
        });

        // Retry the last search with the challenge token
        if (state.lastSearchValue) {
          fetchLocations(state.lastSearchValue, true);
        }
      }
    },
    [
      state.lastSearchValue,
      fetchLocations,
      setVerificationToken,
      setVerificationProvider
    ]
  );

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      fetchLocations.cancel();
    };
  }, [fetchLocations]);

  return {
    ...state,
    fetch: fetchLocations,
    onCaptchaVerify
  };
}
